import Box from "@/ui/atoms/Box";
import Typography from "@/ui/atoms/Typography";
import React from "react";
import type { TTableLocation } from "@/apis/odin/apiODIN";
import Dropdown from "@/ui/molecules/Dropdown";
import { columnToLetter } from "@/utils/excel";
import isEqual from "lodash/isEqual.js";

const renderFoundTable = (
    { worksheetIndex, worksheetName, tableBounds }: TTableLocation,
    isPdf: boolean,
): string => {
    if (isPdf) {
        return worksheetName;
    }
    const { start: colStart, end: colEnd } = tableBounds.columnRange;
    const { start: headerRowStart } = tableBounds.headerRowRange ?? {
        start: null,
    };
    const { start: contentsRowStart, end: rowEnd } =
        tableBounds.contentsRowRange;
    const rowStart = headerRowStart ?? contentsRowStart;
    const colStartLetter = columnToLetter(colStart);
    const colEndLetter = columnToLetter(colEnd);
    return `(${worksheetIndex + 1}) ${worksheetName} - ${colStartLetter}${rowStart}:${colEndLetter}${rowEnd}`;
};

export type ExtractionTableSelectorProps = JSX.IntrinsicAttributes & {
    allFoundTables: Array<TTableLocation>;
    selectedTable: TTableLocation | null;
    onTableChange: (table: TTableLocation) => void;
    isPdf?: boolean;
};

const ExtractionTableSelector: React.FC<ExtractionTableSelectorProps> = ({
    allFoundTables,
    selectedTable,
    onTableChange,
    isPdf = false,
}) => {
    const dropdownOptions = allFoundTables.map((foundTable, i) => ({
        value: i,
        label: renderFoundTable(foundTable, isPdf),
    }));
    const selectedOption = allFoundTables.findIndex(
        foundTable =>
            selectedTable !== null &&
            foundTable.worksheetIndex === selectedTable.worksheetIndex &&
            isEqual(
                foundTable.tableBounds.columnRange,
                selectedTable.tableBounds.columnRange,
            ) &&
            isEqual(
                foundTable.tableBounds.headerRowRange,
                selectedTable.tableBounds.headerRowRange,
            ) &&
            isEqual(
                foundTable.tableBounds.contentsRowRange,
                selectedTable.tableBounds.contentsRowRange,
            ),
    );
    return (
        <Box>
            <Typography variant="h3">Table</Typography>
            <Box sx={{ paddingTop: 2, paddingBottom: 1 }}>
                Select the table you want to extract from this document:
            </Box>
            <Dropdown
                id="table-selector"
                options={dropdownOptions}
                value={selectedOption >= 0 ? selectedOption : null}
                onChange={i =>
                    i !== selectedOption &&
                    allFoundTables[i] &&
                    onTableChange(allFoundTables[i])
                }
                hideLabel={true}
            />
        </Box>
    );
};

export default ExtractionTableSelector;
